import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useFormat } from '../../context/FormatContext';
import FrontMatterEditor from './FrontMatterEditor';
import CoverUpload from './CoverUpload';

const { FiArrowRight, FiInfo, FiBookOpen, FiImage, FiAlertTriangle, FiRefreshCw } = FiIcons;

const StructureStep = () => {
  const {
    pipelineState, updateStructure, proceedToPrintConfig, activeProject, setPipelineState,
    retryAnalysis, loading
  } = useFormat();
  const { structure } = pipelineState;
  const [activeTab, setActiveTab] = useState('structure');

  if (!structure) {
    return (
      <div className="max-w-4xl mx-auto py-16 text-center">
        {pipelineState?.analysisError ? (
          <div className="max-w-xl mx-auto rounded-2xl border border-amber-200 bg-amber-50 p-6">
            <SafeIcon icon={FiAlertTriangle} className="text-3xl text-amber-600 mx-auto mb-3" />
            <p className="text-slate-700 font-semibold">{pipelineState.analysisError}</p>
            <button
              type="button"
              onClick={retryAnalysis}
              disabled={loading}
              className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-bold text-white disabled:opacity-50"
            >
              <SafeIcon icon={FiRefreshCw} />
              Retry Analysis
            </button>
          </div>
        ) : (
          <>
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-500 font-medium">Loading manuscript structure...</p>
          </>
        )}
      </div>
    );
  }

  const tabs = [
    { id: 'structure', label: 'Chapter Structure' },
    { id: 'cover', label: 'Cover Image' },
    { id: 'front', label: 'Front Matter' },
  ];

  const handleCoverUpdated = (newCoverPath) => {
    setPipelineState(prev => ({ ...prev, coverPath: newCoverPath }));
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-5xl mx-auto py-8">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-serif text-slate-800 mb-3">Smart Chapter Engine</h2>
        <p className="text-slate-500">Add your cover, toggle sections, author your front matter, then configure your print layout.</p>
      </div>

      <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 flex items-start space-x-3 mb-6">
        <SafeIcon icon={FiBookOpen} className="text-blue-500 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-blue-800 font-medium">
          <strong>Back Matter</strong> content is managed globally in your <strong>Back Matter Library</strong> on the dashboard. Toggle back matter sections on below and your saved library content will be automatically included in the export.
        </p>
      </div>

      <div className="flex space-x-1 bg-slate-100 p-1 rounded-2xl mb-8 overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 min-w-[150px] py-3 px-4 rounded-xl text-sm font-bold uppercase tracking-widest transition-all ${
              activeTab === tab.id ? 'bg-white text-primary shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {activeTab === 'structure' && (
          <motion.div key="structure" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <StructurePanel structure={structure} updateStructure={updateStructure} />
          </motion.div>
        )}
        {activeTab === 'cover' && (
          <motion.div key="cover" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <CoverUpload
              manuscriptId={activeProject?.id}
              existingCoverPath={pipelineState.coverPath || activeProject?.cover_path}
              onCoverUpdated={handleCoverUpdated}
            />
          </motion.div>
        )}
        {activeTab === 'front' && (
          <motion.div key="front" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <FrontMatterEditor />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex justify-end mt-8">
        <button
          onClick={proceedToPrintConfig}
          className="bg-primary hover:bg-blue-800 text-white px-8 py-4 rounded-xl font-bold uppercase tracking-widest text-sm transition-all shadow-lg flex items-center"
        >
          <span>Configure Print Layout</span>
          <SafeIcon icon={FiArrowRight} className="ml-2" />
        </button>
      </div>
    </motion.div>
  );
};

const StructurePanel = ({ structure, updateStructure }) => {
  const renderSection = (sectionKey, title, description) => (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm mb-6 overflow-hidden">
      <div className="bg-slate-50 px-6 py-4 border-b border-slate-200">
        <h3 className="font-bold text-slate-800">{title}</h3>
        <p className="text-xs text-slate-500 mt-1">{description}</p>
      </div>
      <div className="divide-y divide-slate-100">
        {(structure[sectionKey] || []).map(item => (
          <div key={item.id} className="p-4 px-6 flex items-center justify-between hover:bg-slate-50 transition-colors">
            <div>
              <span className="font-medium text-slate-800">{item.title}</span>
              {item.word_count && (
                <span className="ml-3 text-xs text-slate-400">{item.word_count} words</span>
              )}
            </div>
            <label className="flex items-center cursor-pointer">
              <div className="relative">
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={item.included !== false}
                  onChange={e => updateStructure(sectionKey, item.id, e.target.checked)}
                />
                <div className={`block w-10 h-6 rounded-full transition-colors ${item.included !== false ? 'bg-primary' : 'bg-slate-300'}`}></div>
                <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${item.included !== false ? 'transform translate-x-4' : ''}`}></div>
              </div>
            </label>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div>
        {renderSection('front_matter', 'Front Matter Builder', 'Pages appearing before the main text.')}
        {renderSection('back_matter', 'Back Matter Builder', 'Toggle sections to include content from your Back Matter Library.')}
      </div>
      <div>
        {renderSection('chapters', 'Detected Chapters', 'The core body of your manuscript.')}
      </div>
    </div>
  );
};

export default StructureStep;